import { yupResolver } from '@hookform/resolvers/yup'
import {
  ArrowRight,
  CheckCircleOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
  RestartAltOutlined,
  Warning,
} from '@mui/icons-material'
import Timeline from '@mui/lab/Timeline'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from '@mui/lab/TimelineOppositeContent'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import { Box, Button, Collapse, IconButton, Typography } from '@mui/material'
import {
  ComponentType,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { FormProvider, useForm } from 'react-hook-form'

interface FormComponentsType {
  formName: string
  formObject: ComponentType<any>
  expanded?: boolean
  isValid?: boolean
  errors?: any
  shouldRender?: boolean
}

export default function TimelineForm({
  formParts,
  submitData,
  formSchema,
  defaultValues,
  stepFields,
  ...props
}: {
  formParts: FormComponentsType[]
  submitData: any
  formSchema: any
  defaultValues: any
  stepFields: string[][]
  currentStep: number
  setCurrentStep: Dispatch<SetStateAction<number>>
  companyLocation: string
  setCompanyLocation: Dispatch<SetStateAction<string>>
  formData: any
  setFormData: Dispatch<any>
  contextFields: any
  setBilledFromParent?: any
  selectedPlatform: number
  setSelectedPlatform: Dispatch<SetStateAction<number>>
}): any {
  const [expandedStates, setExpandedStates] = useState(
    formParts.map((form: FormComponentsType) => form.expanded ?? false),
  )
  const boxRefs = useRef<(HTMLDivElement | null)[]>([])
  const [maxWidth, setMaxWidth] = useState<number>(0)
  const { isPending } = submitData
  const toggle = (idx: number) => {
    setExpandedStates((states: any) => {
      const newStates = states.map((_: any, i: number) => i === idx)
      return newStates
    })
    props.setCurrentStep(idx)
  }

  useEffect(() => {
    const widths = boxRefs.current.map((ref) => ref?.offsetWidth || 0)
    setMaxWidth(Math.max(...widths, 0))
  }, [formParts.length, expandedStates])

  useEffect(() => {
    console.log('maxWidth', maxWidth)
  }, [maxWidth, expandedStates])

  const methods = useForm({
    resolver: yupResolver(formSchema),
    context: props.contextFields,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: defaultValues,
    shouldUnregister: false,
  })

  const {
    formState: { errors },
  } = methods

  const handleNext = async () => {
    const fields = (stepFields[props.currentStep] || []) as Parameters<
      typeof methods.trigger
    >[0]

    const isValid = await methods.trigger(fields)
    if (isValid) {
      props.setCurrentStep((s: number) => s + 1)
    } else {
      return Promise.reject({ message: 'Invalid Form' })
    }
  }

  function getStepErrors(stepIndex: number) {
    const fields = stepFields[stepIndex]

    return (
      fields &&
      fields
        .map((fieldName) => {
          const parts = fieldName.split('.')
          let err = errors
          for (const part of parts) {
            err = err?.[part]
          }
          return err ? { field: fieldName, error: err } : null
        })
        .filter(Boolean)
    )
  }

  function resetStepFields(stepIndex: number) {
    const fields = stepFields[stepIndex]
    fields.forEach((fieldName) => {
      const isIdField = fieldName.toLowerCase().endsWith('id')
      methods.resetField(fieldName, {
        defaultValue: isIdField ? null : undefined,
      })
    })
  }

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(async () => {
            try {
              await submitData.mutate(methods.getValues())
            } catch (err) {
              console.log('error')
            }
          })}
          onChange={() => {
            props.setFormData(methods.getValues())
          }}
        >
          <Timeline
            sx={{
              padding: 0,
              marginY: 0,
              [`& .${timelineOppositeContentClasses.root}`]: {
                flex: 0.01,
              },
            }}
          >
            {formParts.map((f: FormComponentsType, index: number) => {
              const FormComponent = f.formObject
              console.log('shouldRender', f.formName, f.shouldRender)
              return f.shouldRender ? (
                <TimelineItem key={`${f.formName}-${index}`}>
                  <TimelineOppositeContent></TimelineOppositeContent>
                  <TimelineSeparator
                    sx={{ marginTop: '20px', marginBottom: '-20px' }}
                  >
                    {index === 0 ? (
                      <TimelineDot color="success" />
                    ) : index === formParts.length - 1 ? (
                      <TimelineDot color="error" />
                    ) : (
                      <TimelineDot />
                    )}
                    {index !== formParts.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Box
                      sx={{
                        backgroundColor: '#042938',
                        color: '#fff',

                        borderRadius: '8px',
                        boxShadow: '2',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '20px',
                          borderRadius: '0 0 8px 8px',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: '10px' }}>
                          <Typography>
                            {f && f.formName.toUpperCase()}
                          </Typography>
                          {index <= props.currentStep &&
                          getStepErrors(index) &&
                          getStepErrors(index).length === 0 ? (
                            <CheckCircleOutline sx={{ color: '#00ae00' }} />
                          ) : getStepErrors(index) &&
                            getStepErrors(index).length > 0 ? (
                            <Warning sx={{ color: '#eba203ff' }} />
                          ) : null}
                        </Box>
                        <IconButton
                          onClick={() => toggle(index)}
                          sx={{ color: '#fff', padding: 0 }}
                        >
                          {expandedStates[index] ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )}
                        </IconButton>
                      </Box>
                      <Collapse
                        in={expandedStates[index]}
                        sx={{ borderRadius: '0 0 8px 8px' }}
                        easing={{
                          enter: 'ease-in',
                          exit: 'ease-out',
                        }}
                      >
                        <Box
                          ref={(el) => ((boxRefs.current[index] as any) = el)}
                          sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            padding: '20px',
                            width: 'auto',
                            borderRadius: '0 0 8px 8px',
                          }}
                        >
                          <Box>
                            <FormComponent {...props} />
                            <Box sx={{ mt: 2 }}>
                              {stepFields[index]?.map((fieldName) => {
                                const error = methods.formState.errors

                                const fieldParts = fieldName.split('.')
                                let fieldError: any = error
                                for (const part of fieldParts) {
                                  if (
                                    fieldError &&
                                    typeof fieldError === 'object'
                                  ) {
                                    fieldError = fieldError[part]
                                  } else {
                                    fieldError = undefined
                                    break
                                  }
                                }
                                return fieldError?.message ? (
                                  <Typography
                                    key={fieldName}
                                    color="error"
                                    variant="body2"
                                  >
                                    {fieldError.message}
                                  </Typography>
                                ) : null
                              })}
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              gap: '10px',
                            }}
                          >
                            <Button
                              onClick={() => {
                                resetStepFields(index)
                              }}
                              sx={{
                                backgroundColor: '#4c6777',
                                color: '#fff',
                                '&:hover': {
                                  backgroundColor: '#406280',
                                },

                                width: '48px',
                                padding: '0',
                              }}
                            >
                              <RestartAltOutlined
                                sx={{ margin: '0 !important' }}
                              />
                            </Button>
                            <Button
                              disabled={getStepErrors(index).length > 0}
                              onClick={async () => {
                                if (
                                  getStepErrors(props.currentStep).length === 0
                                ) {
                                  try {
                                    await handleNext()
                                    toggle(index)
                                    toggle(index + 1)
                                  } catch (err) {
                                    console.log('error:', err as Error)
                                  }
                                }
                              }}
                              sx={{
                                backgroundColor: '#00a700',
                                color: '#fff',
                                '&:hover': {
                                  backgroundColor: '#008f00',
                                },
                              }}
                            >
                              Next
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              ) : (
                <></>
              )
            })}
          </Timeline>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingX: '50px',
            }}
          >
            <Button
              type="submit"
              aria-label="submit"
              disabled={Object.entries(errors).length > 0 || isPending}
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#00a700',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#008f00',
                },
              }}
              variant="contained"
            >
              Create
              {!isPending ? (
                <ArrowRight sx={{ paddingBottom: '2px' }} />
              ) : (
                <></>
              )}
            </Button>
          </Box>
        </form>
      </FormProvider>
    </>
  )
}
